import { motion } from "framer-motion";
import {
  ArrowDown,
  CalendarCheck,
  CalendarX,
  CheckCircle2,
  ChevronRight,
  MonitorSmartphone,
  PhoneMissed,
  Star,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaButton from "@/components/CtaButton";
import PhoneMockup from "@/components/PhoneMockup";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import FaqSection, { FAQS } from "@/components/FaqSection";

const HERO_LINES = ["Stop losing jobs", "to missed calls", "and a bad website."];

const TRUST_ITEMS = [
  "Free preview before you pay anything",
  "Built for roofers, plumbers, HVAC, lawn and pest pros",
  "Based near Indianapolis — you work directly with me",
];

const PAINS = [
  {
    icon: PhoneMissed,
    text: "You miss calls on a roof or under a sink.",
  },
  {
    icon: MonitorSmartphone,
    text: "Your website is outdated, broken, or doesn't exist.",
  },
  {
    icon: CalendarX,
    text: "Customers can't book or request a quote online.",
  },
  {
    icon: Star,
    text: "Happy customers never leave a Google review.",
  },
];

const SERVICES = [
  {
    icon: MonitorSmartphone,
    title: "Website",
    blurb: "A fast, mobile-first site that makes it easy to call, text, or request a quote.",
    testId: "service-card-website",
  },
  {
    icon: PhoneMissed,
    title: "Missed-Call Text-Back",
    blurb: "An automatic text within seconds of any missed call.",
    testId: "service-card-missed-call",
  },
  {
    icon: CalendarCheck,
    title: "Online Booking and Lead Pipeline",
    blurb: "Every lead lands in one pipeline. Customers book a time themselves.",
    testId: "service-card-booking",
  },
  {
    icon: Star,
    title: "Google Review Requests",
    blurb: "After each completed job, every customer gets a polite text or email with a direct review link.",
    testId: "service-card-reviews",
  },
];

const STEPS = [
  { n: "01", title: "I build a free preview of your new site." },
  { n: "02", title: "I send you a short video walkthrough." },
  { n: "03", title: "15-minute call — keep what you like, change what you don't." },
  { n: "04", title: "Launch in under two weeks, then I maintain it monthly." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function scrollToHowItWorks(e) {
  e.preventDefault();
  const el = document.getElementById("how-it-works");
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Home() {
  return (
    <>
      <Seo
        title="Web Pilot | Websites and Automation for Indianapolis Home Service Pros"
        description="Web Pilot builds fast mobile websites and missed-call text-back automation for Indianapolis home service pros. Get a free preview before you pay."
        jsonLd={faqJsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy" data-testid="hero-section">
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.14]"
          aria-hidden="true"
          viewBox="0 0 1440 900"
          fill="none"
          preserveAspectRatio="xMidYMid slice"
        >
          <path
            d="M-40 720 C 300 560, 620 840, 940 640 S 1380 420, 1500 500"
            stroke="#2D8CFF"
            strokeWidth="2"
            strokeDasharray="8 12"
          />
          <circle cx="940" cy="640" r="6" fill="#2D8CFF" />
        </svg>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-center gap-14 py-16 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mb-6 inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-skyblue"
              >
                Websites + automation for home service pros
              </motion.p>

              <h1
                data-testid="hero-heading"
                className="font-display text-4xl font-extrabold leading-[1.07] tracking-tight text-white sm:text-5xl lg:text-6xl"
              >
                {HERO_LINES.map((line, i) => (
                  <span key={i} className="block overflow-hidden pb-1">
                    <motion.span
                      className="block"
                      initial={{ y: "110%" }}
                      animate={{ y: 0 }}
                      transition={{
                        duration: 0.85,
                        delay: 0.15 + i * 0.13,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.55 }}
                className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
              >
                Web Pilot builds Indianapolis home service pros a fast mobile
                website, plus automation that texts back missed calls, books
                appointments, and asks for Google reviews — so leads don't slip
                away while you're on a job.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-7"
              >
                <CtaButton to="/contact" testId="hero-cta-button">
                  Get My Free Website Preview
                </CtaButton>
                <a
                  href="#how-it-works"
                  onClick={scrollToHowItWorks}
                  data-testid="hero-secondary-link"
                  className="inline-flex min-h-12 items-center justify-center gap-2 text-sm font-semibold text-slate-200 underline decoration-skyblue decoration-2 underline-offset-8 transition-colors hover:text-white"
                >
                  See how it works
                  <ArrowDown className="h-4 w-4 text-skyblue" aria-hidden="true" />
                </a>
              </motion.div>
            </div>

            <div className="lg:justify-self-end">
              <PhoneMockup />
            </div>
          </div>

          {/* Trust strip */}
          <div
            className="grid gap-4 border-t border-white/10 py-8 sm:grid-cols-3"
            data-testid="trust-strip"
          >
            {TRUST_ITEMS.map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 + i * 0.12 }}
                className="flex items-start gap-3"
              >
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-skyblue"
                  aria-hidden="true"
                />
                <span className="text-sm font-medium text-slate-200">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* Sound familiar? */}
      <section className="bg-offwhite py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              The daily reality
            </p>
            <h2
              data-testid="sound-familiar-heading"
              className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl"
            >
              Sound familiar?
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PAINS.map((p, i) => (
              <Reveal key={p.text} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col gap-4 rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-skyblue-soft">
                    <p.icon className="h-5 w-5 text-skyblue" aria-hidden="true" />
                  </div>
                  <p className="font-display text-lg font-bold leading-snug text-navy">
                    {p.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              Services
            </p>
            <h2
              data-testid="what-you-get-heading"
              className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl"
            >
              What you get
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="h-full">
                <a
                  href="/services"
                  data-testid={s.testId}
                  className="group flex h-full flex-col gap-4 rounded-xl border border-slate-200/80 bg-offwhite p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white shadow-sm">
                    <s.icon className="h-5 w-5 text-skyblue" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-lg font-bold leading-snug text-navy">
                    {s.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-600">{s.blurb}</p>
                  <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-semibold text-skyblue">
                    See details
                    <ChevronRight
                      className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="scroll-mt-20 bg-offwhite py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              How it works
            </p>
            <h2
              data-testid="how-it-works-heading"
              className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl"
            >
              From first hello to launch
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="border-t-2 border-dashed border-skyblue/50 pt-6">
                  <p className="font-display text-5xl font-extrabold text-skyblue">
                    {s.n}
                  </p>
                  <p className="mt-4 font-display text-base font-bold leading-snug text-navy sm:text-lg">
                    {s.title}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Honesty */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <div
              className="relative overflow-hidden rounded-2xl bg-navy p-8 sm:p-12 lg:p-16"
              data-testid="honesty-section"
            >
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.14]"
                aria-hidden="true"
                viewBox="0 0 1200 400"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M-20 320 C 260 200, 520 380, 760 240 S 1120 120, 1230 180"
                  stroke="#2D8CFF"
                  strokeWidth="2"
                  strokeDasharray="8 10"
                />
              </svg>
              <div className="relative max-w-3xl">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
                  Full disclosure
                </p>
                <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                  Web Pilot is new. That's why the preview is free.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
                  I'd rather show you real work than ask you to take my word for
                  it. I'll build you a preview of your actual site before you pay
                  anything. If you don't like it, you've lost nothing but a few
                  minutes on the phone.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-offwhite py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              FAQ
            </p>
            <h2
              data-testid="faq-heading"
              className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl"
            >
              Frequently asked questions
            </h2>
          </Reveal>
          <FaqSection />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
